"use client";

import { useCallback } from "react";

import { getUserEncryptionKeysFn } from "~/lib/functions/user-encryption-keys";
import type { KeyGrants } from "~/lib/schemas/key-grants";

import {
  base64UrlToBuffer,
  encryptData,
  generateDek,
  wrapDek,
  type EncryptedData,
} from "./crypto";
import type { KeyManager } from "./key-manager";
import type { ShareTarget } from "./shared-grants";

/** A record's fields encrypted under one new key, and that key wrapped for each reader. */
export interface EncryptedSave<K extends string> {
  /** JSON-serialized EncryptedData per field, for the encrypted columns. */
  fields: Record<K, string>;
  /** The first field's IV, for the table's `iv` column. */
  iv: string;
  /** The new key for the save to write with the record, in one transaction. */
  keyGrants: KeyGrants;
}

async function importPublicKey(publicKey: string): Promise<CryptoKey> {
  return crypto.subtle.importKey(
    "raw",
    base64UrlToBuffer(publicKey),
    { name: "ECDH", namedCurve: "P-256" },
    true,
    [],
  );
}

/**
 * Encrypt every field with one new key and wrap that key for the person
 * saving and for each person the record is shared with. Nothing is stored
 * here: the save sends `keyGrants` with its record, so a failed save leaves
 * the old text and its keys as they were.
 *
 * A reader whose public key is unusable is skipped with a warning, so one
 * broken key cannot block the save.
 */
export async function encryptForSave<K extends string>(
  plaintexts: Record<K, string>,
  ownPublicKey: string,
  shareWith: readonly ShareTarget[],
): Promise<EncryptedSave<K>> {
  const dek = await generateDek();

  // Each field gets its own IV, so no nonce is used twice under one key.
  const entries = Object.entries(plaintexts) as Array<[K, string]>;
  const encryptedEntries = await Promise.all(
    entries.map(async ([key, plaintext]): Promise<[K, EncryptedData]> => [
      key,
      await encryptData(plaintext, dek),
    ]),
  );

  const fields = {} as Record<K, string>;
  let iv = "";
  for (const [key, encrypted] of encryptedEntries) {
    fields[key] = JSON.stringify(encrypted);
    if (!iv) iv = encrypted.iv;
  }

  const own = JSON.stringify(
    await wrapDek(dek, await importPublicKey(ownPublicKey)),
  );
  // The person saving can be listed as a reader too, such as a coach in a
  // program with their own account; `own` already gives them the key.
  const readers = shareWith.filter(
    (target) => target.publicKey !== ownPublicKey,
  );
  const shared = await Promise.all(
    readers.map(async (target) => {
      try {
        const wrapped = await wrapDek(
          dek,
          await importPublicKey(target.publicKey),
        );
        return [
          { userId: target.userId, encryptedDek: JSON.stringify(wrapped) },
        ];
      } catch (error) {
        console.warn("Skipped a reader whose public key is unusable", {
          userId: target.userId,
          error,
        });
        return [];
      }
    }),
  );

  return { fields, iv, keyGrants: { own, shared: shared.flat() } };
}

/**
 * Hook for encrypting a record before it is saved. Every call makes a new
 * key, so a shared record lists its readers in `shareWith` on every save.
 */
export function useEncryptOnSave(keyManager: KeyManager | null): {
  encryptFields: <K extends string>(
    plaintexts: Record<K, string>,
    shareWith?: readonly ShareTarget[],
  ) => Promise<EncryptedSave<K>>;
} {
  const encryptFields = useCallback(
    async <K extends string>(
      plaintexts: Record<K, string>,
      shareWith: readonly ShareTarget[] = [],
    ): Promise<EncryptedSave<K>> => {
      if (!keyManager) throw new Error("KeyManager is locked");
      const keys = await getUserEncryptionKeysFn();
      if (!keys) throw new Error("Encryption keys not found");
      return encryptForSave(plaintexts, keys.publicKey, shareWith);
    },
    [keyManager],
  );

  return { encryptFields };
}
